import * as React from 'react';
import { connect } from 'react-redux';
import classnames from 'classnames';
import { Moment } from 'moment';
import isEmpty from 'lodash/isEmpty';
import isFunction from 'lodash/isFunction';
import isNil from 'lodash/isNil';

import PromptExit from 'libs/router/prompt-exit/PromptExit';
import { navigate } from 'libs/router';

import { Form, FormActions, FormField } from 'components/form';
import Input from 'components/inputs/input/Input';
import InputRadio from 'components/inputs/input-radio/InputRadio';
import InputToggles from 'components/inputs/input-toggles/InputToggles';
import InputArray from 'components/inputs/input-array/InputArray';
import PageHeading from 'components/page/page-heading/PageHeading';
import ActionButton from 'components/buttons/action-button/ActionButton';
import ActionLink from 'components/buttons/action-link/ActionLink';
import DatePickerSingle from 'components/date-picker/date-picker-single/DatePickerSingleLazy';
import MixedSelectDropdown from 'components/inputs/mixed-select-dropdown/MixedSelectDropdown';
import Banner from 'components/banners/banner/Banner';
import Tooltip from 'components/Tooltip';
import { withModal } from 'components/modal/ModalHOC';

import CampaignMainImageUpload from 'modules/campaigns/components/upload/CampaignMainImageUpload';
import CampaignAssets from 'modules/campaigns/components/CampaignAssets';
import CampaignPackage from 'modules/campaigns/components/campaign-package/CampaignPackage';
import CampaignTimeline from 'modules/campaigns/components/campaign-timeline/components/creatorfull/CampaignTimeline';
import CcmCampaignProductionBudget from 'modules/campaigns/components/ccm/components/production-budget/CcmCampaignProductionBudget';
import CampaignTutorialFreeform from 'modules/campaigns/components/campaign-tutorial/freeform/CampaignTutorialFreeform';
import CcmCampaignBriefModal from 'modules/campaigns/components/ccm/components/modals/brief/CcmCampaignBriefModal';

import { fetchCampaignBrief } from 'modules/campaigns/reducers/campaignBrief';
import { fetchRealmOutfitsRegions, resetRealmOutfitsRegions } from 'modules/realm-outfits/reducers/realm-outfits-regions';
import { fetchTeamMembers, resetTeamMembers } from 'modules/organizations/reducers/teamMembers';
import { saveAssets } from 'modules/campaigns/reducers/campaignAssets';
import {
  flushCcmCampaignTimeline,
  generateCcmCampaignTimeline,
  saveCcmCampaign
} from '../../../../redux/actions';

import { isCcmModerator } from 'utils/account';
import { __t, applyTranslations } from 'utils/language';
import { logError } from 'utils/log';
import { FASHION_STYLES } from 'utils/influencers';
import { getFullName } from 'utils/view';
import { sanitizeGenericHtml } from 'utils/sanitize';
import {
  NAME_MAX_LENGTH,
  isArrayRequired,
  isGreaterThanOrEqualTo,
  isIntegerMoneySum,
  isLesserThanOrEqualTo,
  isPositiveNumber,
  isRequired,
  isZalandoCollectionLink,
  maxLength
} from 'utils/validators';
import {
  MAX_NUM_CHARS_BRIEF,
  MAX_NUM_EXCLUDED_BRANDS,
  MAX_NUM_TAGS,
  getStatusLabelTextKey
} from '../../../../utils/ccm-campaign';
import {
  getInitialVisibleMonth,
  isDayBlocked
} from '../../utils/date-picker';

import './ccm-campaign-form-creatorfull.scss';

applyTranslations(require('locales/common/collabary-common.i18n.json'));
applyTranslations(require('./ccm-campaign-form-creatorfull.i18n.json'));

import type {
  Action,
  Campaign,
  CampaignAsset,
  CampaignAssetState,
  MixedSelectDropdownItem,
  PromiseAction,
  Role,
  TeamMemberMapped
} from 'types';
import type {
  CcmCampaignFormDetails,
  CcmCampaignTimeline
} from '../../../../types';
import type {
  CampaignPackageToDetails,
  CampaignPackageType
} from 'modules/campaigns/components/campaign-package/types';

type OwnProps = {
  campaign: Campaign | null,
  pageHeading: JSX.Element | string | null,
  organizationId: number,
  brandProfileId: number,
  onCloseModal?: () => PromiseAction
};

type StateProps = {
  timeline: CcmCampaignTimeline | null,
  assets: CampaignAssetState,
  regions: Array<string>,
  teamMembers: Array<MixedSelectDropdownItem>,
  packageToDetailsMap: CampaignPackageToDetails,
  minProductionBudget: number,
  maxProductionBudget: number,
  roles: Array<Role>
};

type DispatchProps = {
  dispatchFetchCampaign: (campaignId: number) => Promise<void>,
  dispatchSaveCcmCampaign: (
    brandProfileId: number,
    formDetails: CcmCampaignFormDetails,
    shouldUpdate: boolean
  ) => Promise<Campaign>,
  dispatchSaveAssets: (campaignId: number) => Promise<Action>,
  dispatchGenerateCampaignTimeline: (date: string) => void,
  dispatchFetchRealmOutfitsRegions: () => void,
  dispatchFetchTeamMembers: (organizationId: number) => void,
  dispatchResetRealmOutfitsRegions: () => void,
  dispatchResetTeamMembers: (organizationId: number) => void,
  dispatchFlushCcmCampaignTimeline: () => void
};

type Props = OwnProps & StateProps & DispatchProps;

const COMMON_MSG_KEY = 'collabary_common';
const MSG_KEY = 'ccm_campaign_form_creatorfull';

const ActionLinkWithBriefModal = withModal(ActionLink, {
  modalContent: CcmCampaignBriefModal
});

const getActiveTeamMembers = (
  campaign: Campaign | null,
  teamMembers: Array<TeamMemberMapped>
): Array<TeamMemberMapped> => {
  const managers = (campaign?.managers || [])
    .map(manager => String(manager.userId));

  return teamMembers.filter(member => managers.includes(member.value));
};

const getTeamMembers = (
  campaign: Campaign | null,
  teamMembers: Array<TeamMemberMapped>
): Array<MixedSelectDropdownItem> => {
  const activeTeamMembers = getActiveTeamMembers(campaign, teamMembers)
    .map(member => member.value);

  return teamMembers
    .filter(member => !isNil(member.value))
    .map(member => ({
      identifier: member.value,
      label: member.label,
      value: member.value,
      isDisabled: false,
      isFocused: false,
      isSelected: activeTeamMembers.includes(member.value)
    }));
};

const getInitialFormDetails = (
  campaign: Campaign | null
): CcmCampaignFormDetails => ({
  id: campaign?.id ?? null,
  name: campaign?.name ?? '',
  campaignPackage: campaign?.packageName ?? 'm',
  contentProductionBudget: campaign?.contentProductionBudget ?? null,
  startDate: campaign?.startDate ?? null,
  countries: campaign?.countries ?? [],
  collectionLink: campaign?.collectionLink ?? '',
  genders: (!isEmpty(campaign?.genders) ? (campaign?.genders || [])[0] : ''),
  fashionStyles: campaign?.fashionStyles ?? [],
  description: campaign?.description ?? '',
  excludedBrands: campaign?.excludedBrands ?? [],
  teamMembers: [] as Array<MixedSelectDropdownItem>,
  mainImage: campaign?.mainImage ?? null as CampaignAsset | undefined | null
});

const CcmCampaignFormCreatorfull: React.FC<Props> = ({
  campaign,
  pageHeading,
  organizationId,
  brandProfileId,
  timeline,
  assets,
  regions,
  teamMembers,
  packageToDetailsMap,
  minProductionBudget,
  maxProductionBudget,
  roles,
  onCloseModal,
  dispatchFetchCampaign,
  dispatchSaveCcmCampaign,
  dispatchSaveAssets,
  dispatchGenerateCampaignTimeline,
  dispatchFetchRealmOutfitsRegions,
  dispatchFetchTeamMembers,
  dispatchResetRealmOutfitsRegions,
  dispatchResetTeamMembers,
  dispatchFlushCcmCampaignTimeline
}): JSX.Element => {
  const [formDetails, setFormDetails] = React.useState<CcmCampaignFormDetails>(getInitialFormDetails(campaign));

  const [formState, setFormState] = React.useState({
    isEditMode: !isNil(campaign),
    isUpdated: false,
    isSubmitting: false,
    isValid: true,
    collectionUrlError: ''
  });

  const getCountries = (): Array<MixedSelectDropdownItem> => (
    regions.map(region => ({
      label: region.toUpperCase(),
      value: region,
      identifier: region,
      isDisabled: false,
      isFocused: false,
      isSelected: (formDetails.countries || []).includes(region)
    }))
  );

  const isModerator = isCcmModerator(roles);

  const getCalculatedProductionBudget = (
    type: 'min' | 'max',
    productionBudget: string | number | null | undefined
  ): number | null => {
    if (isNil(formDetails.campaignPackage)) {
      return null;
    }

    // allow only 0-9
    const safeProductionBudget = Number(String(productionBudget || 0).replace(/\D/g, ''));

    if (type === 'min') {
      return safeProductionBudget > 0 ? safeProductionBudget : null;
    }

    return safeProductionBudget * packageToDetailsMap[formDetails.campaignPackage].numOutfits;
  };

  const getTimeline = (): CcmCampaignTimeline | null => (
    isNil(timeline) ? (campaign?.timeline ?? null) : timeline
  );

  const shouldShowTimeline = (): boolean => (
    formState.isEditMode || (!formState.isEditMode && !isNil(timeline))
  );

  const updateFormState = (
    key: 'isUpdated' | 'isSubmitting' | 'isValid',
    value: boolean = true
  ): void => {
    setFormState({
      ...formState,
      [key]: value
    });
  };

  const handleFieldChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    const target = event.currentTarget;
    const identifier = target.dataset.identifier || null;
    let value = target.value || '' as string | number | null;

    if (isNil(identifier)) {
      return;
    }

    if (identifier === 'contentProductionBudget' && isEmpty(value)) {
      value = null;
    }

    updateFormState('isUpdated');
    setFormDetails({
      ...formDetails,
      [identifier]: value
    });
  };

  const handleCountriesChange = (
    items: Array<MixedSelectDropdownItem>
  ): void => {
    updateFormState('isUpdated');
    setFormDetails({
      ...formDetails,
      countries: items
        .filter(item => item.isSelected)
        .map(item => item.value)
    });
  };

  const handleTeamMembersChange = (
    items: Array<MixedSelectDropdownItem>
  ): void => {
    updateFormState('isUpdated');
    setFormDetails({
      ...formDetails,
      teamMembers: items
    });
  };

  const handleScrollCollectionUrlIntoView = (): void => (
    document
      .querySelector('.ccm-campaign-form-creatorfull__collection-link')
      ?.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      })
  );

  const handleSubmit = (): void => {
    const validation = isZalandoCollectionLink(formDetails.collectionLink);
    const isValid = validation?.valid ?? true; // treat as valid if validation is undefined

    if (!isValid) {
      setFormState({
        ...formState,
        isValid,
        collectionUrlError: validation?.message
      });

      handleScrollCollectionUrlIntoView();
    } else {
      updateFormState('isSubmitting');

      dispatchSaveCcmCampaign(brandProfileId, {
        ...formDetails,
        contentProductionBudget: Number(formDetails.contentProductionBudget),
        mainImage: assets.mainImage
      }, formState.isEditMode).then(updatedCampaign => (
        dispatchSaveAssets(updatedCampaign?.id))
        .then(() => dispatchFetchCampaign(updatedCampaign?.id))
        .then(() => {
          if (formState.isEditMode && isFunction(onCloseModal)) {
            onCloseModal();
          }

          navigate.to(`/campaigns/ccm/${updatedCampaign?.id}/overview`);
        })
        .catch((error) => logError(error))
      );
    }
  };

  const renderCalendarInfo = (): React.ReactNode => (
    <div className="ccm-campaign-form-creatorfull__calendar-info">
      {__t(`${MSG_KEY}.calendar.weekend_disclaimer`)}
    </div>
  );

  const renderActionLinkWithBriefModal = (
    text: string,
    classModifier?: string
  ): React.ReactNode => {
    const actionLinkClasses = classnames('ccm-campaign-form-creatorfull__tutorial-link', {
      [`ccm-campaign-form-creatorfull__tutorial-link--modifier-${classModifier}`]: Boolean(classModifier)
    });

    return (
      <ActionLinkWithBriefModal
        size="normal"
        color="secondary"
        decoration="none"
        className={actionLinkClasses}
      >
        {text}
      </ActionLinkWithBriefModal>
    );
  };

  const renderSectionThreeTutorial = (): React.ReactNode => (
    <CampaignTutorialFreeform
      title={__t(`${MSG_KEY}.tutorial.section_three.title`)}
    >
      <ul>
        <li>
          {__t(`${MSG_KEY}.tutorial.section_three.bullets.one`)}
        </li>
        <li>
          {__t(`${MSG_KEY}.tutorial.section_three.bullets.two`)}
        </li>
        <li>
          {__t(`${MSG_KEY}.tutorial.section_three.bullets.three`)}
          {renderActionLinkWithBriefModal(
            __t(`${MSG_KEY}.tutorial.section_three.brief_examples_link_text`),
            'black'
          )}.
        </li>
      </ul>
    </CampaignTutorialFreeform>
  );

  const renderName = (): React.ReactNode => (
    <FormField
      showRequiredMark
      showValidationIcon={false}
      fontSize="small"
      label={__t(`${MSG_KEY}.name.label`)}
      sublabel={__t(`${MSG_KEY}.name.sublabel`)}
      labelType="highlighted"
      direction="column"
      validators={[
        isRequired(formDetails.name),
        maxLength(formDetails.name, NAME_MAX_LENGTH)
      ]}
    >
      <div className="ccm-campaign-form-creatorfull__name">
        <Input
          name="name"
          width="responsive"
          value={formDetails.name}
          placeholder={__t(`${MSG_KEY}.name.placeholder`)}
          data={{ identifier: 'name' }}
          onChange={handleFieldChange}
        />
      </div>
    </FormField>
  );

  const renderPackage = (): React.ReactNode => (
    <FormField
      showRequiredMark
      showValidationIcon={false}
      fontSize="small"
      label={__t(`${MSG_KEY}.package.label`)}
      sublabel={__t(`${MSG_KEY}.package.sublabel`)}
      labelType="highlighted"
      direction="column"
      validators={[
        isRequired(formDetails.campaignPackage)
      ]}
    >
      <div className="ccm-campaign-form-creatorfull__package">
        <CampaignPackage
          types={['s', 'm', 'l']}
          packageToDetailsMap={packageToDetailsMap}
          selected={formDetails.campaignPackage}
          disabled={formState.isEditMode}
          onSelect={(type: CampaignPackageType) => {
            updateFormState('isUpdated');
            setFormDetails({
              ...formDetails,
              campaignPackage: type
            });
          }}
        />
      </div>
    </FormField>
  );

  const renderProductionBudget = (): React.ReactNode => {
    const productionBudget = formDetails.contentProductionBudget;
    const textKey = campaign ? getStatusLabelTextKey(campaign) : null;
    const isDraftCampaign = textKey === 'draft';

    return (
      <FormField
        showRequiredMark
        showValidationIcon={false}
        fontSize="small"
        label={__t(`${MSG_KEY}.production_budget.label`)}
        sublabel={__t(`${MSG_KEY}.production_budget.sublabel`, { minValue: minProductionBudget })}
        labelType="highlighted"
        direction="column"
        validators={[
          isRequired(productionBudget),
          isPositiveNumber(productionBudget),
          isIntegerMoneySum(productionBudget),
          isGreaterThanOrEqualTo(productionBudget, minProductionBudget, __t(`${MSG_KEY}.errors.min_value`, {
            minValue: minProductionBudget
          })),
          isLesserThanOrEqualTo(productionBudget, maxProductionBudget, __t(`${MSG_KEY}.errors.max_value`, {
            maxValue: maxProductionBudget
          }))
        ]}
      >
        <CcmCampaignProductionBudget
          minProductionBudget={getCalculatedProductionBudget('min', productionBudget)}
          maxProductionBudget={getCalculatedProductionBudget('max', productionBudget)}
          packageToDetailsMap={packageToDetailsMap}
          campaignPackage={formDetails.campaignPackage}
          disabled={formState.isEditMode && !(isModerator && isDraftCampaign)}
          onFieldChange={handleFieldChange}
        />
      </FormField>
    );
  };

  const renderDatePicker = (
    cannotEditStartDate: boolean
  ): React.ReactNode => (
    <DatePickerSingle
      name="date"
      date={formDetails.startDate}
      disabled={cannotEditStartDate}
      calendarInfo={renderCalendarInfo()}
      isDayBlocked={(day: Moment) => isDayBlocked(
        day,
        formState.isEditMode
          ? campaign?.startDate ?? null
          : null
      )}
      initialVisibleMonth={() => getInitialVisibleMonth(
        formState.isEditMode
          ? campaign?.startDate ?? null
          : null
      )}
      onChange={(date?: string | null) => {
        updateFormState('isUpdated');
        setFormDetails({
          ...formDetails,
          startDate: date
        });

        if (typeof date === 'string') {
          dispatchGenerateCampaignTimeline(date);
        }
      }}
    />
  );

  const renderTimeline = (): React.ReactNode => {
    const cannotEditStartDate = (campaign?.participantsCount || 0) > 0;

    return (
      <FormField
        showRequiredMark
        showValidationIcon={false}
        fontSize="small"
        label={__t(`${MSG_KEY}.timeline.label`)}
        sublabel={__t(`${MSG_KEY}.timeline.sublabel`)}
        labelType="highlighted"
        direction="column"
        validators={[
          isRequired(formDetails.startDate)
        ]}
      >
        <div className="ccm-campaign-form-creatorfull__timeline-date">
          <div className="ccm-campaign-form-creatorfull__timeline-date-label">
            {__t(`${MSG_KEY}.timeline.start_date_label`)}:
          </div>
          {
            cannotEditStartDate ? (
              <Tooltip
                message={__t(`${MSG_KEY}.tooltips.cannot_edit_start_date`)}
              >
                {renderDatePicker(cannotEditStartDate)}
              </Tooltip>
            ) : (
              <React.Fragment>
                {renderDatePicker(cannotEditStartDate)}
              </React.Fragment>
            )
          }
        </div>
        {
          shouldShowTimeline() && (
            <div className="ccm-campaign-form-creatorfull__timeline">
              <CampaignTimeline
                timeline={getTimeline()}
              />
            </div>
          )
        }
      </FormField>
    );
  };

  const renderCountries = (): React.ReactNode => (
    <FormField
      showRequiredMark
      showValidationIcon={false}
      fontSize="small"
      label={__t(`${MSG_KEY}.countries.label`)}
      sublabel={__t(`${MSG_KEY}.countries.sublabel`)}
      labelType="highlighted"
      direction="column"
      validators={[
        isArrayRequired(formDetails.countries)
      ]}
    >
      <div className="ccm-campaign-form-creatorfull__countries">
        <MixedSelectDropdown
          name="ccm-campaign-form-creatorfull__countries"
          items={getCountries()}
          placeholder={__t(`${MSG_KEY}.countries.placeholder`)}
          withMulti
          withRemovable
          withSortable
          withCheckboxes
          withSelectAll
          onItemsToggle={handleCountriesChange}
          onItemsRemove={handleCountriesChange}
          onItemsRemoveAll={handleCountriesChange}
          onItemsToggleAll={handleCountriesChange}
        />
      </div>
    </FormField>
  );

  const renderCollectionLink = (): React.ReactNode => (
    <FormField
      showRequiredMark
      showValidationIcon={false}
      fontSize="small"
      label={__t(`${MSG_KEY}.collection_link.label`)}
      sublabel={__t(`${MSG_KEY}.collection_link.sublabel`)}
      labelType="highlighted"
      direction="column"
      validators={[
        isRequired(formDetails.collectionLink)
      ]}
    >
      <div className="ccm-campaign-form-creatorfull__collection-link">
        <Input
          name="collectionLink"
          width="responsive"
          value={formDetails.collectionLink}
          placeholder={__t(`${MSG_KEY}.collection_link.placeholder`)}
          data={{ identifier: 'collectionLink' }}
          onChange={handleFieldChange}
        />
        {
          !formState.isValid && formState.collectionUrlError && (
            <div className="ccm-campaign-form-creatorfull__collection-link--error">
              {formState.collectionUrlError}
            </div>
          )
        }
      </div>
    </FormField>
  );

  const renderTargetGroup = (): React.ReactNode => (
    <FormField
      showRequiredMark
      showValidationIcon={false}
      fontSize="small"
      label={__t(`${MSG_KEY}.genders.label`)}
      sublabel={__t(`${MSG_KEY}.genders.sublabel`)}
      labelType="highlighted"
      direction="column"
      validators={[
        isRequired(formDetails.genders)
      ]}
    >
      <div className="ccm-campaign-form-creatorfull__target-group">
        {
          ['women', 'men'].map(type => (
            <InputRadio
              key={`ccm-campaign-form-creatorfull__target-group--${type}`}
              id={`ccm-campaign-form-creatorfull__target-group--${type}`}
              name="ccm-campaign-form-creatorfull__target-group"
              label={__t(`${MSG_KEY}.genders.radios.${type}`)}
              value={type}
              checked={type === formDetails.genders}
              labelClassName="ccm-campaign-form-creatorfull__target-group-label"
              data={{ identifier: 'genders' }}
              onChange={handleFieldChange}
            />
          ))
        }
      </div>
    </FormField>
  );

  const renderTags = (): React.ReactNode => {
    const items = FASHION_STYLES.map(value => ({
      value,
      label: __t(`${COMMON_MSG_KEY}.fashion_styles.${value}`)
    }));

    return (
      <FormField
        showRequiredMark
        showValidationIcon={false}
        fontSize="small"
        label={__t(`${MSG_KEY}.fashion_styles.label`)}
        sublabel={__t(`${MSG_KEY}.fashion_styles.sublabel`)}
        labelType="highlighted"
        direction="column"
        validators={[
          isArrayRequired(formDetails.fashionStyles)
        ]}
      >
        <div className="ccm-campaign-form-creatorfull__tags">
          <InputToggles
            items={items}
            value={formDetails.fashionStyles}
            limit={MAX_NUM_TAGS}
            onChange={(fashionStyles: Array<string>) => {
              updateFormState('isUpdated');
              setFormDetails({
                ...formDetails,
                fashionStyles
              });
            }}
          />
        </div>
      </FormField>
    );
  };

  const renderMainImage = (): React.ReactNode => (
    <FormField
      showRequiredMark
      showValidationIcon={false}
      fontSize="small"
      label={__t(`${MSG_KEY}.main_image.label`)}
      sublabel={__t(`${MSG_KEY}.main_image.sublabel`)}
      labelType="highlighted"
      direction="column"
      validators={[
        isRequired(assets.mainImage)
      ]}
    >
      <div className="ccm-campaign-form-creatorfull__main-image">
        <CampaignMainImageUpload
          mainImage={assets.mainImage}
        />
      </div>
    </FormField>
  );

  const renderBrief = (): React.ReactNode => {
    const SublabelComponent = (
      <React.Fragment>
        {__t(`${MSG_KEY}.description.sublabel`)}
        {renderActionLinkWithBriefModal(
          __t(`${MSG_KEY}.description.brief_examples_link_text`)
        )}.
      </React.Fragment>
    );

    return (
      <FormField
        showRequiredMark
        showValidationIcon={false}
        fontSize="small"
        label={__t(`${MSG_KEY}.description.label`)}
        sublabel={SublabelComponent}
        labelType="highlighted"
        direction="column"
        validators={[
          isRequired(formDetails.description),
          maxLength(formDetails.description, MAX_NUM_CHARS_BRIEF)
        ]}
      >
        <div className="ccm-campaign-form-creatorfull__brief">
          <Input
            multiline
            width="responsive"
            value={formDetails.description}
            placeholder={__t(`${MSG_KEY}.description.placeholder`)}
            charCount={{
              curCount: formDetails.description.length,
              maxChars: MAX_NUM_CHARS_BRIEF
            }}
            data={{ identifier: 'description' }}
            onChange={handleFieldChange}
          />
        </div>
      </FormField>
    );
  };

  const renderInspirationalMaterial = (): React.ReactNode => (
    <FormField
      showValidationIcon={false}
      fontSize="small"
      label={__t(`${MSG_KEY}.inspirational_material.label`)}
      sublabel={__t(`${MSG_KEY}.inspirational_material.sublabel`)}
      labelType="highlighted"
      direction="column"
    >
      <div className="ccm-campaign-form-creatorfull__inspirational-material">
        <CampaignAssets
          canEdit
          assets={assets.assets || []}
        />
      </div>
    </FormField>
  );

  const renderExcludedBrands = (): React.ReactNode => (
    <FormField
      showValidationIcon={false}
      fontSize="small"
      label={__t(`${MSG_KEY}.excluded_brands.label`)}
      sublabel={(
        <span dangerouslySetInnerHTML={{ __html: sanitizeGenericHtml(__t(`${MSG_KEY}.excluded_brands.sublabel`)) }} />
      )}
      labelType="highlighted"
      direction="column"
    >
      <div className="ccm-campaign-form-creatorfull__excluded-brands">
        <InputArray
          name="ccm-campaign-form-creatorfull__excluded-brands"
          placeholder={__t(`${MSG_KEY}.excluded_brands.placeholder`)}
          value={formDetails.excludedBrands}
          max={MAX_NUM_EXCLUDED_BRANDS}
          supportWhiteSpace
          onChange={(excludedBrands: Array<string>) => {
            updateFormState('isUpdated');
            setFormDetails({
              ...formDetails,
              excludedBrands
            });
          }}
        />
      </div>
    </FormField>
  );

  const renderTeamMembersBanner = (): React.ReactNode => (
    <div className="ccm-campaign-form-creatorfull__campaign-team-banner">
      <Banner
        background="on-white"
      >
        <div className="ccm-campaign-form-creatorfull__campaign-team-banner-title">
          {__t(`${MSG_KEY}.team_members.banner.title`)}
        </div>
        <div className="ccm-campaign-form-creatorfull__campaign-team-banner-description">
          {__t(`${MSG_KEY}.team_members.banner.description`)}
        </div>
      </Banner>
    </div>
  );

  const renderTeamMembers = (): React.ReactNode => {
    const updatedTeamMembers = !isEmpty(formDetails.teamMembers)
      ? formDetails.teamMembers
      : teamMembers;

    return (
      <FormField
        showValidationIcon={false}
        fontSize="small"
        label={__t(`${MSG_KEY}.team_members.label`)}
        sublabel={__t(`${MSG_KEY}.team_members.sublabel`)}
        labelType="highlighted"
        direction="column"
      >
        <div className="ccm-campaign-form-creatorfull__team-members">
          <MixedSelectDropdown
            name="ccm-campaign-form-creatorfull__team-members"
            items={updatedTeamMembers}
            placeholder={__t(`${MSG_KEY}.team_members.placeholder`)}
            withMulti
            withRemovable
            withSortable
            withCheckboxes
            withSelectAll
            withSearchable
            onItemsToggle={handleTeamMembersChange}
            onItemsRemove={handleTeamMembersChange}
            onItemsRemoveAll={handleTeamMembersChange}
            onItemsToggleAll={handleTeamMembersChange}
          />
        </div>
      </FormField>
    );
  };

  const renderButtons = (): React.ReactNode => (
    <FormActions
      align="center"
      containerClass="ccm-campaign-form-creatorfull__form-actions"
    >
      <ActionButton
        color="tertiary"
        onClick={() => {
          if (formState.isEditMode && isFunction(onCloseModal)) {
            onCloseModal();
            return;
          }

          navigate.to(`/organizations/${organizationId}/campaigns`);
        }}
      >
        {__t(`${COMMON_MSG_KEY}.action_button.cancel`)}
      </ActionButton>
      <ActionButton
        color="primary"
        type="submit"
      >
        {__t(`${COMMON_MSG_KEY}.action_button.submit`)}
      </ActionButton>
    </FormActions>
  );

  React.useEffect(() => {
    setFormDetails(getInitialFormDetails(campaign));

    return () => {
      dispatchFlushCcmCampaignTimeline();
    };
  }, [campaign?.id]);

  React.useEffect(() => {
    dispatchFetchRealmOutfitsRegions();

    return () => {
      dispatchResetRealmOutfitsRegions();
    };
  }, []);

  React.useEffect(() => {
    dispatchFetchTeamMembers(organizationId);

    return () => {
      dispatchResetTeamMembers(organizationId);
    };
  }, []);

  const sectionClasses = classnames('ccm-campaign-form-creatorfull__section', {
    'ccm-campaign-form-creatorfull__section--edit-mode': formState.isEditMode
  });

  const formClasses = classnames('ccm-campaign-form-creatorfull__form', {
    'ccm-campaign-form-creatorfull__form--edit-mode': formState.isEditMode
  });

  return (
    <div className="ccm-campaign-form-creatorfull">
      <Form
        onSubmit={handleSubmit}
      >
        {
          !formState.isSubmitting && (formState.isUpdated || assets.isUpdated) && (
            <PromptExit type="page" />
          )
        }
        {
          typeof pageHeading === 'string' ? (
            <PageHeading
              heading={pageHeading}
              color="light"
            />
          ) : null
        }
        {
          !isNil(pageHeading) ? (pageHeading) : null
        }
        <div className="ccm-campaign-form-creatorfull__sections">
          <div className={sectionClasses}>
            <div className={formClasses}>
              {renderName()}
              {renderPackage()}
              {renderProductionBudget()}
              {renderTimeline()}
              {renderCountries()}
            </div>
          </div>
          <div className={sectionClasses}>
            <div className={formClasses}>
              {renderCollectionLink()}
              {renderTargetGroup()}
              {renderTags()}
            </div>
          </div>
          <div className={sectionClasses}>
            <div className={formClasses}>
              {renderMainImage()}
              {renderBrief()}
              {renderInspirationalMaterial()}
              {renderExcludedBrands()}
            </div>
            {
              !formState.isEditMode && (
                <div className="ccm-campaign-form-creatorfull__info">
                  {renderSectionThreeTutorial()}
                </div>
              )
            }
          </div>
          <div className={sectionClasses}>
            <div className={formClasses}>
              {renderTeamMembersBanner()}
              {renderTeamMembers()}
            </div>
          </div>
          <div className={sectionClasses}>
            <div className={formClasses}>
              {renderButtons()}
            </div>
          </div>
        </div>
      </Form>
    </div>
  );
};

const mapMemberToSelectItem = (member) => ({
  value: String(member.id),
  label: getFullName(member)
});

const mapStateToProps = ({
  campaignAssets,
  ccmCampaign,
  organizations,
  realmOutfitsRegions,
  runtimeConfig,
  teamMembers,
  account
}, { campaign }): StateProps => {
  const updatedTeamMembers = teamMembers.mapByOrganizationId[organizations.currentOrganizationId] || [];
  const mappedTeamMembers: Array<TeamMemberMapped> = updatedTeamMembers.map(mapMemberToSelectItem);
  const configValues = runtimeConfig?.config || {};

  return {
    assets: campaignAssets,
    regions: realmOutfitsRegions.regions,
    timeline: ccmCampaign.timeline,
    teamMembers: getTeamMembers(campaign, mappedTeamMembers),
    roles: account.roles,
    packageToDetailsMap: {
      s: {
        promotionBudget: configValues.ccmCampaignPackageSBudget,
        numOutfits: configValues.ccmCampaignPackageSOutfits,
        numOutfitsRange: configValues.ccmCampaignPackageSOutfitsRange,
        numOutfitClicks: configValues.ccmCampaignPackageSOutfitClicks,
        numOutfitViews: configValues.ccmCampaignPackageSOutfitViews,
        numWeeks: configValues.ccmCampaignPackageSNumWeeks
      },
      m: {
        promotionBudget: configValues.ccmCampaignPackageMBudget,
        numOutfits: configValues.ccmCampaignPackageMOutfits,
        numOutfitsRange: configValues.ccmCampaignPackageMOutfitsRange,
        numOutfitClicks: configValues.ccmCampaignPackageMOutfitClicks,
        numOutfitViews: configValues.ccmCampaignPackageMOutfitViews,
        numWeeks: configValues.ccmCampaignPackageMNumWeeks
      },
      l: {
        promotionBudget: configValues.ccmCampaignPackageLBudget,
        numOutfits: configValues.ccmCampaignPackageLOutfits,
        numOutfitsRange: configValues.ccmCampaignPackageLOutfitsRange,
        numOutfitClicks: configValues.ccmCampaignPackageLOutfitClicks,
        numOutfitViews: configValues.ccmCampaignPackageLOutfitViews,
        numWeeks: configValues.ccmCampaignPackageLNumWeeks
      }
    },
    minProductionBudget: configValues.ccmCampaignContentProductionBudgetMin,
    maxProductionBudget: configValues.ccmCampaignContentProductionBudgetMax
  };
};

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchFetchCampaign: (campaignId: number, showLoader: boolean = false) => (
    dispatch(fetchCampaignBrief(campaignId, showLoader, false))
  ),
  dispatchSaveCcmCampaign: (
    brandProfileId: number,
    formDetails: CcmCampaignFormDetails,
    shouldUpdate: boolean
  ) => (
    dispatch(saveCcmCampaign(brandProfileId, formDetails, shouldUpdate))
  ),
  dispatchSaveAssets: (campaignId: number) => (
    dispatch(saveAssets(campaignId))
  ),
  dispatchGenerateCampaignTimeline: (date: string) => (
    dispatch(generateCcmCampaignTimeline(date))
  ),
  dispatchFetchRealmOutfitsRegions: () => (
    dispatch(fetchRealmOutfitsRegions())
  ),
  dispatchFetchTeamMembers: (organizationId: number) => (
    dispatch(fetchTeamMembers(organizationId))
  ),
  dispatchResetRealmOutfitsRegions: () => (
    dispatch(resetRealmOutfitsRegions())
  ),
  dispatchResetTeamMembers: (organizationId: number) => (
    dispatch(resetTeamMembers(organizationId))
  ),
  dispatchFlushCcmCampaignTimeline: () => (
    dispatch(flushCcmCampaignTimeline())
  )
});

export default connect(mapStateToProps, mapDispatchToProps)(CcmCampaignFormCreatorfull);
